import React, { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";

const STORAGE_KEY = "qgl_age_verified_v1";

/**
 * One-time age + research-use confirmation gate.
 *
 * • Shows a full-screen dark modal on first visit
 * • Confirmation persists in localStorage, so it does NOT re-appear on
 *   subsequent page navigations or return visits
 * • "Under 18 / Leave" redirects the visitor away from the site
 * • Rendered above every public page; admin routes handle their own layout
 */
export const AgeGate = () => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            const v = localStorage.getItem(STORAGE_KEY);
            if (!v) setOpen(true);
        } catch (e) {
            // localStorage unavailable (Safari private mode) — still show the gate
            setOpen(true);
        }
    }, []);

    if (!open) return null;

    const confirm = () => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, ts: Date.now() })); } catch (e) { /* noop */ }
        setOpen(false);
    };

    const leave = () => {
        // Redirect off-site. window.history.length may be 1 (came from QR), so replace instead of back().
        window.location.replace("https://www.google.com/");
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="age-gate-title"
            data-testid="age-gate-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center px-5 sm:px-6"
            style={{
                background: "rgba(5,5,10,0.86)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
            }}
        >
            <div
                className="w-full max-w-md rounded-2xl border border-white/10 overflow-hidden shadow-[0_30px_80px_-20px_rgba(99,102,241,0.4)]"
                style={{
                    background: "linear-gradient(180deg, #0A0A12 0%, #0C0C1A 100%)",
                }}
            >
                {/* Header band */}
                <div
                    className="px-6 py-5 border-b border-white/10"
                    style={{
                        background: "linear-gradient(135deg, rgba(99,102,241,0.14), rgba(139,92,246,0.10))",
                    }}
                >
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-[0_0_22px_rgba(99,102,241,0.55)] shrink-0">
                            <ShieldCheck className="w-4 h-4 text-white" />
                        </div>
                        <div className="leading-tight min-w-0">
                            <p className="text-[10px] tracking-[0.28em] uppercase text-indigo-400">
                                Quantum Genesis Labs
                            </p>
                            <h2
                                id="age-gate-title"
                                className="text-[15px] sm:text-base font-medium text-white truncate"
                            >
                                Age Verification &amp; Research Use Confirmation
                            </h2>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="px-6 py-6 space-y-4">
                    <p className="text-[13.5px] sm:text-sm text-slate-300 leading-[1.65]">
                        This website contains products intended for laboratory research purposes.
                        Access is restricted to persons <span className="text-white font-medium">18 years of age or older</span>.
                    </p>
                    <p className="text-[13.5px] sm:text-sm text-slate-400 leading-[1.65]">
                        Products are not intended for human consumption, veterinary use, diagnosis,
                        treatment, or prevention of disease.
                    </p>
                </div>

                {/* Actions */}
                <div className="px-6 pb-6 pt-1 flex flex-col-reverse sm:flex-row gap-3">
                    <button
                        type="button"
                        onClick={leave}
                        data-testid="age-gate-leave"
                        className="flex-1 h-11 rounded-full border border-white/12 bg-white/[0.03] hover:bg-white/[0.06] text-slate-300 hover:text-white text-sm font-medium tracking-tight transition-colors"
                    >
                        Under 18 / Leave
                    </button>
                    <button
                        type="button"
                        onClick={confirm}
                        data-testid="age-gate-confirm"
                        className="flex-1 h-11 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium tracking-tight transition-colors shadow-[0_0_24px_-6px_rgba(99,102,241,0.7)]"
                    >
                        I&apos;m 18+ and understand
                    </button>
                </div>

                <p className="px-6 pb-5 text-[10.5px] text-slate-600 tracking-wide text-center">
                    For Research Purposes Only · Not for human consumption
                </p>
            </div>
        </div>
    );
};

export default AgeGate;
